// src/components/dashboard/SyncCalendarButton.tsx
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { interviewService } from "@/services/serviceFactory";
import { toast } from "@/hooks/use-toast";

interface SyncCalendarButtonProps {
  onSyncComplete: () => void;
  disabledExternally?: boolean;
}

const SyncCalendarButton: React.FC<SyncCalendarButtonProps> = ({
  onSyncComplete,
  disabledExternally = false,
}) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [disableReason, setDisableReason] = useState<string | null>(null);

  React.useEffect(() => {
    // Fetch user profile once to determine auth method
    (async () => {
      try {
        const profile = await interviewService.getUserProfile();
        // Ensure property exists before accessing to satisfy TypeScript when demo mode omits auth_method
        if (
          "auth_method" in profile &&
          profile.auth_method &&
          profile.auth_method !== "microsoft"
        ) {
          setDisableReason(
            "🙈 Ivy has not been approved for calendar access yet."
          );
        }
      } catch (err) {
        console.error("Failed to fetch profile in SyncCalendarButton", err);
      }
    })();
  }, []);

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      const response: {
        success: boolean;
        message: string;
        addedCount: number;
        updatedCount?: number;
        cancelledCount?: number;
        completedCount?: number;
      } = await interviewService.syncCalendarEvents();

      // Handle successful response
      if (response.success) {
        // Get counts, defaulting to 0 if undefined
        const addedCount = response.addedCount || 0;
        const updatedCount = response.updatedCount || 0;
        const cancelledCount = response.cancelledCount || 0;
        const completedCount = response.completedCount || 0;

        // Create detailed message
        let details = [];
        if (addedCount > 0)
          details.push(`${addedCount} new interviews imported`);
        if (updatedCount > 0)
          details.push(`${updatedCount} interviews updated`);
        if (cancelledCount > 0)
          details.push(`${cancelledCount} interviews cancelled`);
        if (completedCount > 0)
          details.push(`${completedCount} past interviews marked as completed`);

        const description =
          details.length > 0
            ? `AI successfully processed: ${details.join(", ")}.`
            : "No changes found in your calendar.";

        toast({
          title: "AI Calendar Sync Complete",
          description,
          variant: "default",
        });

        // Always refresh the interviews list
        onSyncComplete();
      } else {
        // Handle error response
        toast({
          title: "Sync Failed",
          description: response.message || "Failed to sync calendar events.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error syncing calendar:", error);
      toast({
        title: "Sync Error",
        description:
          "An unexpected error occurred while syncing your calendar.",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Wrap button in span so tooltip works even when button is disabled */}
          <span className="inline-block" tabIndex={0}>
            <Button
              onClick={handleSync}
              disabled={isSyncing || !!disableReason || disabledExternally}
              variant="outline"
              className={`
                !h-auto !py-1 bg-green-600 border-green-600 text-white 
                hover:bg-green-700 hover:border-green-700 hover:text-white 
                transition-all duration-300 relative overflow-hidden
                ${
                  !isSyncing
                    ? "before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-1000"
                    : ""
                }
              `}
            >
              {isSyncing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  AI Processing...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Smart sync
                </>
              )}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <p className="text-center">
            <strong>AI-Powered Calendar Sync</strong>
            <br />
            {disableReason ??
              (disabledExternally
                ? "Smart sync is only available for active projects that you own."
                : "Automatically imports new calendar events, checks for cancellations, and uses AI to extract interview details.")}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default SyncCalendarButton;
